// src/pages/Customer/Cart.jsx - WITH SPECIAL INSTRUCTIONS
import React, { useState, useMemo, useCallback } from 'react';
import { X, ShoppingCart, Loader, Minus, Plus, MessageSquare } from 'lucide-react';

const CustomerCart = ({ cart, updateQuantity, placeOrder, onBack, onOrderPlaced }) => {
  const [customerName, setCustomerName] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [placing, setPlacing] = useState(false);

  // ✅ MEMOIZED: Calculate total only when cart changes
  const total = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [cart]);

  // ✅ MEMOIZED: Calculate item count
  const itemCount = useMemo(() => cart.length, [cart.length]);

  // ✅ OPTIMIZED: Memoized handlers
  const handleIncrement = useCallback((itemId) => {
    updateQuantity(itemId, 1);
  }, [updateQuantity]);

  const handleDecrement = useCallback((itemId) => {
    updateQuantity(itemId, -1);
  }, [updateQuantity]);

  const handleNameChange = useCallback((e) => {
    setCustomerName(e.target.value);
  }, []);

  const handleInstructionsChange = useCallback((e) => {
    setSpecialInstructions(e.target.value);
  }, []);

  const handleCheckoutToggle = useCallback(() => {
    setShowCheckout(prev => !prev);
  }, []);

  const handlePlaceOrder = useCallback(async (e) => {
    e.preventDefault();
    
    if (!customerName.trim()) {
      alert('Please enter your name');
      return;
    }
    
    try {
      setPlacing(true);
      
      // Pass special instructions along with customer name
      const result = await placeOrder(customerName.trim(), specialInstructions.trim());
      
      if (result && result.sessionId) {
        setShowCheckout(false);
        setCustomerName('');
        setSpecialInstructions('');
        
        if (onOrderPlaced) {
          onOrderPlaced(result.sessionId);
        }
      }
    } catch (error) {
      console.error('❌ Error in handlePlaceOrder:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  }, [customerName, specialInstructions, placeOrder, onOrderPlaced]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 sticky top-0 z-40 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="text-white hover:text-orange-100 transition-colors"
            aria-label="Back to menu"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Your Cart</h1>
            <p className="text-orange-100 text-sm">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Cart Items */}
      <div className="max-w-4xl mx-auto p-6">
        {itemCount > 0 ? (
          <>
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>
              <div className="space-y-4">
                {cart.map(item => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                  />
                ))}
              </div>
            </div>

            {/* Total & Checkout */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky bottom-6">
              <div className="flex justify-between items-center mb-4 pb-4 border-b">
                <span className="text-xl font-bold text-gray-800">Total Amount:</span>
                <span className="text-2xl font-bold text-orange-600">₹{total}</span>
              </div>
              <button
                onClick={handleCheckoutToggle}
                disabled={placing}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        ) : (
          <EmptyCart onBack={onBack} />
        )}
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          total={total}
          itemCount={itemCount}
          customerName={customerName}
          specialInstructions={specialInstructions}
          placing={placing}
          onNameChange={handleNameChange}
          onInstructionsChange={handleInstructionsChange}
          onClose={handleCheckoutToggle}
          onSubmit={handlePlaceOrder}
        />
      )}
    </div>
  );
};

// ✅ OPTIMIZED: Separate CartItem component to prevent unnecessary re-renders
const CartItem = React.memo(({ item, onIncrement, onDecrement }) => {
  const itemTotal = useMemo(() => item.price * item.quantity, [item.price, item.quantity]);

  return (
    <div className="flex justify-between items-center py-4 border-b last:border-b-0 gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-gray-800 truncate">{item.name}</h3>
        <p className="text-gray-600 text-sm">₹{item.price} each</p>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => onDecrement(item.id)}
            className="w-8 h-8 bg-white rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors shadow-sm"
            aria-label="Decrease quantity"
          >
            <Minus className="w-4 h-4 text-gray-700" />
          </button>
          <span className="w-10 text-center font-bold text-gray-800">{item.quantity}</span>
          <button
            onClick={() => onIncrement(item.id)}
            className="w-8 h-8 bg-orange-500 text-white rounded-full hover:bg-orange-600 flex items-center justify-center transition-colors shadow-sm"
            aria-label="Increase quantity"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <span className="font-bold text-orange-600 w-20 text-right">₹{itemTotal}</span>
      </div>
    </div>
  );
});

CartItem.displayName = 'CartItem';

// ✅ OPTIMIZED: Separate EmptyCart component
const EmptyCart = React.memo(({ onBack }) => (
  <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
    <ShoppingCart className="w-20 h-20 text-gray-300 mx-auto mb-4" />
    <h3 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h3>
    <p className="text-gray-600 mb-6">Add some delicious items to get started!</p>
    <button
      onClick={onBack}
      className="bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 font-semibold transition-colors shadow-md"
    >
      Browse Menu
    </button>
  </div>
));

EmptyCart.displayName = 'EmptyCart';

// ✅ OPTIMIZED: Separate CheckoutModal component WITH SPECIAL INSTRUCTIONS
const CheckoutModal = React.memo(({ 
  cart, 
  total, 
  itemCount, 
  customerName,
  specialInstructions,
  placing, 
  onNameChange,
  onInstructionsChange,
  onClose, 
  onSubmit 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
    <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Checkout</h2>
        <button 
          onClick={onClose} 
          className="text-gray-500 hover:text-gray-700 transition-colors"
          disabled={placing}
          aria-label="Close checkout"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={customerName}
            onChange={onNameChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            placeholder="Enter your name"
            required
            disabled={placing}
            autoFocus
          />
        </div>

        {/* ✅ SPECIAL INSTRUCTIONS FIELD */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-orange-500" />
            Special Instructions <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <textarea
            value={specialInstructions}
            onChange={onInstructionsChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
            placeholder="Any special requests? (e.g., less spicy, no onions, extra sauce)"
            rows="3"
            maxLength="200"
            disabled={placing}
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">
              💡 Let us know your preferences
            </p>
            <p className="text-xs text-gray-400">
              {specialInstructions.length}/200
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-5 rounded-xl border border-orange-100">
          <div className="flex justify-between mb-3 text-gray-700">
            <span className="font-medium">Items:</span>
            <span className="font-bold">{itemCount}</span>
          </div>
          <div className="flex justify-between text-xl font-bold pt-3 border-t border-orange-200">
            <span className="text-gray-800">Total:</span>
            <span className="text-orange-600">₹{total}</span>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800 flex items-start gap-2">
            <span className="text-lg">📱</span>
            <span>After placing your order, you'll be able to track its status in real-time!</span>
          </p>
        </div>
        <button
          onClick={onSubmit}
          disabled={placing}
          className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors shadow-md hover:shadow-lg"
        >
          {placing ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Placing Order...
            </>
          ) : (
            'Place Order'
          )}
        </button>
      </div>
    </div>
  </div>
));

CheckoutModal.displayName = 'CheckoutModal';

export default CustomerCart;